import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import {
  User, Camera, Shield, Key, Smartphone, Clock, Trash2,
  Save, Loader2, Eye, EyeOff, LogOut, Globe, BookOpen, GraduationCap, Mail,
  Users, UserPlus, Star, Heart, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import FounderBadge from '../components/FounderBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SectionCard = ({ children, title, icon: Icon, className = '', eyebrow }) => (
  <motion.section
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className={`f500-card p-6 lg:p-10 ${className}`}
  >
    {title && (
      <div className="mb-8 pb-6 border-b border-[var(--f500-line)]">
        {eyebrow && <p className="f500-eyebrow mb-2">— {eyebrow}</p>}
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-[var(--f500-ink)]" strokeWidth={1.75} />}
          <h2 className="f500-h2 text-xl">{title}</h2>
        </div>
      </div>
    )}
    {children}
  </motion.section>
);

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const fileRef = useRef(null);

  // Profile state
  const [profile, setProfile] = useState({ name: '', university: '', program: '', year_of_study: '', bio: '' });
  const [photoUrl, setPhotoUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Security state
  const [passwordForm, setPasswordForm] = useState({ current: '', new_pw: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  // 2FA state
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaStep, setTwoFaStep] = useState('idle'); // idle | sending | verify | disabling
  const [otpCode, setOtpCode] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [disablePw, setDisablePw] = useState('');

  // Login alerts
  const [loginAlerts, setLoginAlerts] = useState(true);

  // Sessions & History
  const [sessions, setSessions] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Security status
  const [securityStatus, setSecurityStatus] = useState(null);

  // Delete account
  const [deleteStep, setDeleteStep] = useState(0);
  const [deletePw, setDeletePw] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Active tab
  const [tab, setTab] = useState('profile');

  // Social data
  const [social, setSocial] = useState(null);
  const [socialLoading, setSocialLoading] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (tab === 'security') {
      loadSessionsData();
      loadHistory();
      loadSecurityStatus();
    }
    if (tab === 'social') {
      loadSocial();
    }
  }, [tab]);

  const loadSocial = async () => {
    setSocialLoading(true);
    try {
      const res = await axios.get(`${API}/social/my-social`);
      setSocial(res.data);
    } catch (e) {
      toast.error('Failed to load social data');
    } finally {
      setSocialLoading(false);
    }
  };

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${API}/profile`);
      const d = res.data;
      setProfile({ name: d.name || '', university: d.university || '', program: d.program || '', year_of_study: d.year_of_study || '', bio: d.bio || '' });
      setPhotoUrl(d.profile_photo_url || '');
      setTwoFaEnabled(d.two_factor_enabled || false);
      setLoginAlerts(d.login_alerts_enabled !== false);
    } catch (e) {
      toast.error('Failed to load profile');
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, profile);
      toast.success('Profile updated');
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be under 5 MB'); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/profile/photo`, fd);
      setPhotoUrl(res.data.url);
      toast.success('Photo uploaded');
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    try {
      await axios.delete(`${API}/profile/photo`);
      setPhotoUrl('');
      toast.success('Photo removed');
    } catch (e) {
      toast.error('Failed to remove photo');
    }
  };

  const changePassword = async () => {
    if (passwordForm.new_pw !== passwordForm.confirm) { toast.error('Passwords do not match'); return; }
    if (passwordForm.new_pw.length < 8) { toast.error('Minimum 8 characters'); return; }
    setChangingPw(true);
    try {
      await axios.put(`${API}/profile/change-password`, { current_password: passwordForm.current, new_password: passwordForm.new_pw });
      toast.success('Password changed');
      setPasswordForm({ current: '', new_pw: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setChangingPw(false);
    }
  };

  const start2faSetup = async () => {
    setTwoFaStep('sending');
    try {
      const res = await axios.post(`${API}/profile/2fa/setup`);
      setEmailHint(res.data.email_hint || '');
      setTwoFaStep('verify');
      if (res.data.code) {
        // Email delivery failed — auto-fill the code for the user
        setOtpCode(res.data.code);
        toast.info('Email delivery unavailable. Code has been auto-filled for you.');
      } else {
        toast.success('Verification code sent to your email');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start 2FA setup');
      setTwoFaStep('idle');
    }
  };

  const verify2fa = async () => {
    setTwoFaStep('sending');
    try {
      await axios.post(`${API}/profile/2fa/verify`, { code: otpCode });
      setTwoFaEnabled(true);
      setTwoFaStep('idle');
      setOtpCode('');
      toast.success('Two-factor authentication enabled!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
      setTwoFaStep('verify');
    }
  };

  const disable2fa = async () => {
    setTwoFaStep('disabling');
    try {
      await axios.post(`${API}/profile/2fa/disable`, { password: disablePw });
      setTwoFaEnabled(false);
      setTwoFaStep('idle');
      setDisablePw('');
      toast.success('Two-factor authentication disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
      setTwoFaStep('idle');
    }
  };

  const toggleLoginAlerts = async () => {
    try {
      const res = await axios.put(`${API}/profile/login-alerts`);
      setLoginAlerts(res.data.login_alerts_enabled);
      toast.success(res.data.login_alerts_enabled ? 'Login alerts enabled' : 'Login alerts disabled');
    } catch {
      toast.error('Failed to update');
    }
  };

  const loadSecurityStatus = async () => {
    try {
      const res = await axios.get(`${API}/profile/security-status`);
      setSecurityStatus(res.data);
    } catch (err) { console.error('Failed to load security status:', err); }
  };

  const loadSessionsData = async () => {
    setSessionsLoading(true);
    try {
      const res = await axios.get(`${API}/profile/sessions`);
      setSessions(res.data.sessions || []);
    } catch (err) { console.error('Failed to load sessions:', err); } finally {
      setSessionsLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/profile/login-history`);
      setLoginHistory(res.data.history || []);
    } catch (err) { console.error('Failed to load login history:', err); } finally {
      setHistoryLoading(false);
    }
  };

  const revokeSession = async (id) => {
    try {
      await axios.delete(`${API}/profile/sessions/${id}`);
      setSessions(s => s.filter(x => x.id !== id));
      toast.success('Session revoked');
    } catch {
      toast.error('Failed to revoke');
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.post(`${API}/profile/delete-account`, { password: deletePw, confirmation: 'DELETE' });
      toast.success('Account deleted. Goodbye.');
      window.location.href = '/';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setDeleting(false);
    }
  };

  const isGoogleAuth = user?.auth_provider === 'google';
  const initials = (profile.name || user?.name || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="f500-page max-w-5xl mx-auto px-4 py-8 lg:py-12 space-y-8" data-testid="profile-page">
      {/* Header */}
      <div className="border-b border-[var(--f500-line)] pb-8 flex items-center gap-6">
        <div className="relative group">
          {photoUrl ? (
            <img 
              src={photoUrl.startsWith('/') ? photoUrl : photoUrl} 
              alt="Profile" 
              className="w-24 h-24 object-cover border border-[var(--f500-line)]" 
              data-testid="profile-photo"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
            />
          ) : null}
          <div className={`w-24 h-24 bg-[var(--f500-ink)] items-center justify-center text-white text-2xl font-bold f500-h1 ${photoUrl ? 'hidden' : 'flex'}`} data-testid="profile-avatar">
            {initials}
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            className="absolute -bottom-2 -right-2 p-2 bg-[var(--f500-blue)] text-white hover:bg-[var(--f500-blue-hover)] transition-colors"
            data-testid="upload-photo-btn"
            title="Change photo"
          >
            {uploadingPhoto ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPhoto} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <p className="f500-eyebrow">— Account / {user?.plan || 'Free'}</p>
            <FounderBadge user={user} />
          </div>
          <h1 className="f500-display text-3xl sm:text-4xl truncate">{profile.name || user?.name || 'Student'}</h1>
          <p className="f500-body text-sm mt-2 truncate">{user?.email}</p>
          {photoUrl && (
            <button onClick={removePhoto} className="text-xs text-[var(--f500-red)] hover:underline mt-2 transition-colors" data-testid="remove-photo-btn">
              Remove photo
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--f500-line)]">
        <div className="flex gap-0 -mb-px">
          {[
            { id: 'profile', label: 'Profile', icon: User },
            { id: 'social', label: 'Social', icon: Users },
            { id: 'security', label: 'Security', icon: Shield },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 ${
                tab === t.id
                  ? 'text-[var(--f500-ink)] border-[var(--f500-blue)]'
                  : 'text-[var(--f500-ink-2)] border-transparent hover:text-[var(--f500-ink)]'
              }`}
              style={{ fontFamily: 'var(--sf-text)' }}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" strokeWidth={1.75} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'profile' && (
        <div className="space-y-6">
          {/* Personal Info */}
          <SectionCard title="Personal Information" icon={User} iconColor="text-violet-400">
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  value={profile.name}
                  onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                  placeholder="Your full name"
                  className="rounded-xl"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user?.email || ''} disabled className="rounded-xl opacity-60" />
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <Label>Bio</Label>
              <textarea
                value={profile.bio}
                onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
                placeholder="Tell us about yourself..."
                maxLength={500}
                rows={3}
                className="w-full rounded-xl bg-transparent border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-[var(--isp-heading)] placeholder:text-[var(--isp-body)]/40 outline-none focus:ring-1 focus:ring-violet-500/30 resize-none"
                data-testid="profile-bio-input"
              />
              <p className="text-xs text-[var(--isp-body)]">{profile.bio.length}/500</p>
            </div>
          </SectionCard>

          {/* Academic Info */}
          <SectionCard title="Academic Details" icon={GraduationCap} iconColor="text-emerald-400">
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label>University / Institution</Label>
                <Input
                  value={profile.university}
                  onChange={e => setProfile(p => ({ ...p, university: e.target.value }))}
                  placeholder="e.g., University of Ghana"
                  className="rounded-xl"
                  data-testid="profile-university-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Course / Program</Label>
                <Input
                  value={profile.program}
                  onChange={e => setProfile(p => ({ ...p, program: e.target.value }))}
                  placeholder="e.g., Computer Science"
                  className="rounded-xl"
                  data-testid="profile-program-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Year of Study</Label>
                <select
                  value={profile.year_of_study}
                  onChange={e => setProfile(p => ({ ...p, year_of_study: e.target.value }))}
                  className="w-full rounded-xl bg-transparent border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-[var(--isp-heading)] outline-none"
                  data-testid="profile-year-select"
                >
                  <option value="">Select year</option>
                  <option value="1st Year">1st Year</option>
                  <option value="2nd Year">2nd Year</option>
                  <option value="3rd Year">3rd Year</option>
                  <option value="4th Year">4th Year</option>
                  <option value="5th Year+">5th Year+</option>
                  <option value="Postgrad">Postgraduate</option>
                </select>
              </div>
            </div>
          </SectionCard>

          <button
            onClick={saveProfile}
            disabled={saving}
            className="f500-btn f500-btn-primary px-8"
            data-testid="save-profile-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      )}

      {tab === 'security' && (
        <div className="space-y-6">
          {/* Security Status Banner */}
          {securityStatus && securityStatus.recent_failed_attempts > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className=" border border-red-500/30 bg-red-500/[0.06] p-5"
              data-testid="security-alert-banner"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-red-500/10">
                  <Shield className="w-5 h-5 text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-300">Recent Suspicious Activity</h3>
                  <p className="text-xs text-red-300/70 mt-1">
                    {securityStatus.recent_failed_attempts} failed login attempt{securityStatus.recent_failed_attempts > 1 ? 's' : ''} detected
                    in the last 15 minutes. If this wasn't you, change your password and enable 2FA.
                  </p>
                  <div className="mt-3 space-y-1.5">
                    {securityStatus.attempts.slice(0, 3).map((a, i) => (
                      <div key={`item-${i}`} className="flex items-center gap-2 text-xs text-red-300/60">
                        <span className="font-mono bg-red-500/10 px-2 py-0.5 rounded">{a.ip}</span>
                        <span>{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Change Password */}
          {!isGoogleAuth && (
            <SectionCard title="Change Password" icon={Key} iconColor="text-amber-400">
              <div className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <div className="relative">
                    <Input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordForm.current}
                      onChange={e => setPasswordForm(p => ({ ...p, current: e.target.value }))}
                      placeholder="Enter current password"
                      className="rounded-xl pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(!showPasswords)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--isp-body)]"
                    >
                      {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    value={passwordForm.new_pw}
                    onChange={e => setPasswordForm(p => ({ ...p, new_pw: e.target.value }))}
                    placeholder="Minimum 8 characters"
                    className="rounded-xl"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    value={passwordForm.confirm}
                    onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                    placeholder="Repeat new password"
                    className="rounded-xl"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button
                  onClick={changePassword}
                  disabled={changingPw || !passwordForm.current || !passwordForm.new_pw}
                  className="rounded-xl gap-2"
                  data-testid="change-password-btn"
                >
                  {changingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                  Change Password
                </Button>
              </div>
            </SectionCard>
          )}

          {/* 2FA */}
          <SectionCard title="Two-Factor Authentication" icon={Smartphone} iconColor="text-cyan-400">
            <p className="text-sm text-[var(--isp-body)] mb-4">
              {twoFaEnabled
                ? 'Two-factor authentication is enabled. You will receive an email code each time you log in.'
                : 'Add an extra layer of security. A verification code will be sent to your email on each login.'}
            </p>
            {twoFaEnabled ? (
              <div className="space-y-3 max-w-md">
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                  <Shield className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-emerald-300 font-medium">2FA is active</span>
                </div>
                {twoFaStep !== 'idle' ? (
                  <div className="space-y-2">
                    <Label>Confirm your password to disable 2FA</Label>
                    <Input
                      type="password"
                      value={disablePw}
                      onChange={e => setDisablePw(e.target.value)}
                      placeholder="Your password"
                      className="rounded-xl"
                      data-testid="disable-2fa-pw"
                    />
                    <div className="flex gap-2">
                      <Button onClick={disable2fa} disabled={!disablePw || twoFaStep === 'disabling'} variant="destructive" className="rounded-xl gap-2" data-testid="confirm-disable-2fa">
                        {twoFaStep === 'disabling' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Disable 2FA
                      </Button>
                      <Button variant="outline" onClick={() => { setTwoFaStep('idle'); setDisablePw(''); }} className="rounded-xl">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => setTwoFaStep('disabling_prompt')} className="rounded-xl text-red-400 border-red-500/20 hover:bg-red-500/10" data-testid="disable-2fa-btn">
                    Disable 2FA
                  </Button>
                )}
              </div>
            ) : twoFaStep === 'verify' ? (
              <div className="space-y-3 max-w-md">
                <p className="text-xs text-[var(--isp-body)]">A 6-digit code was sent to <span className="text-[var(--isp-heading)] font-medium">{emailHint}</span></p>
                <Input
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="rounded-xl text-center text-lg tracking-widest"
                  maxLength={6}
                  data-testid="2fa-otp-input"
                />
                <div className="flex gap-2">
                  <Button onClick={verify2fa} disabled={otpCode.length !== 6 || twoFaStep === 'sending'} className="rounded-xl gap-2 bg-emerald-600 hover:bg-emerald-500" data-testid="verify-2fa-btn">
                    {twoFaStep === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                    Verify &amp; Enable
                  </Button>
                  <Button variant="outline" onClick={() => { setTwoFaStep('idle'); setOtpCode(''); }} className="rounded-xl">Cancel</Button>
                </div>
              </div>
            ) : (
              <Button onClick={start2faSetup} disabled={twoFaStep === 'sending'} className="rounded-xl gap-2" data-testid="enable-2fa-btn">
                {twoFaStep === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Enable 2FA via Email
              </Button>
            )}
          </SectionCard>

          {/* Login Alerts */}
          <SectionCard title="Login Notifications" icon={Mail} iconColor="text-amber-400">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <p className="text-sm text-[var(--isp-body)] leading-relaxed">
                  Receive an email alert when a sign-in is detected from a new IP address or device.
                  This helps you spot unauthorised access early.
                </p>
              </div>
              <button
                onClick={toggleLoginAlerts}
                className={`flex-shrink-0 w-12 h-7 rounded-full transition-colors relative ${
                  loginAlerts ? 'bg-emerald-500' : 'bg-white/10'
                }`}
                data-testid="login-alerts-toggle"
              >
                <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                  loginAlerts ? 'translate-x-[22px]' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
            <p className="text-xs text-[var(--isp-body)]/60 mt-3">
              {loginAlerts ? 'Alerts are ON — you will be emailed on new-device logins.' : 'Alerts are OFF — no emails on new logins.'}
            </p>
          </SectionCard>

          {/* Active Sessions */}
          <SectionCard title="Active Sessions" icon={Globe} iconColor="text-blue-400">
            {sessionsLoading ? (
              <div className="flex items-center gap-2 py-4 text-[var(--isp-body)]"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
            ) : sessions.length === 0 ? (
              <p className="text-sm text-[var(--isp-body)]">No active sessions found.</p>
            ) : (
              <div className="space-y-3">
                {sessions.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-[var(--isp-glass-border)]">
                    <div>
                      <p className="text-sm text-[var(--isp-heading)] font-medium">{s.device || 'Browser Session'}</p>
                      <p className="text-xs text-[var(--isp-body)]">IP: {s.ip_address || 'unknown'} &middot; {s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => revokeSession(s.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg" data-testid={`revoke-session-${s.id}`}>
                      <LogOut className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Login History */}
          <SectionCard title="Login History" icon={Clock} iconColor="text-indigo-400">
            {historyLoading ? (
              <div className="flex items-center gap-2 py-4 text-[var(--isp-body)]"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
            ) : loginHistory.length === 0 ? (
              <p className="text-sm text-[var(--isp-body)]">No login history yet.</p>
            ) : (
              <div className="space-y-2">
                {loginHistory.slice(0, 10).map((h, i) => (
                  <div key={`item-${i}`} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-[var(--isp-glass-border)]">
                    <div className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[var(--isp-heading)]">{h.method === 'google' ? 'Google Sign-In' : 'Email Login'}</p>
                      <p className="text-xs text-[var(--isp-body)] truncate">
                        {h.city || h.country ? (
                          <span className="text-emerald-400/80">{[h.city, h.country].filter(Boolean).join(', ')}</span>
                        ) : null}
                        {h.city || h.country ? ' · ' : ''}
                        IP: {h.ip_address || '—'} · {h.timestamp ? new Date(h.timestamp).toLocaleString() : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Delete Account */}
          <SectionCard title="Danger Zone" icon={Trash2} iconColor="text-red-400" className="border-red-500/20">
            <p className="text-sm text-[var(--isp-body)] mb-4">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            {deleteStep === 0 ? (
              <Button variant="outline" onClick={() => setDeleteStep(1)} className="rounded-xl text-red-400 border-red-500/20 hover:bg-red-500/10" data-testid="delete-account-start">
                Delete My Account
              </Button>
            ) : (
              <div className="space-y-3 max-w-md p-4 rounded-xl border border-red-500/20 bg-red-500/5">
                <p className="text-sm text-red-300 font-medium">Are you sure? This will delete all your subjects, lectures, study materials, and exam data.</p>
                {!isGoogleAuth && (
                  <div className="space-y-2">
                    <Label className="text-red-300">Enter your password</Label>
                    <Input
                      type="password"
                      value={deletePw}
                      onChange={e => setDeletePw(e.target.value)}
                      placeholder="Your password"
                      className="rounded-xl border-red-500/20"
                      data-testid="delete-account-pw"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button onClick={handleDeleteAccount} disabled={deleting || (!isGoogleAuth && !deletePw)} variant="destructive" className="rounded-xl gap-2" data-testid="confirm-delete-account">
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Permanently Delete
                  </Button>
                  <Button variant="outline" onClick={() => { setDeleteStep(0); setDeletePw(''); }} className="rounded-xl">Cancel</Button>
                </div>
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {/* Social Tab */}
      {tab === 'social' && (
        <div className="space-y-6" data-testid="social-tab">
          {socialLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>
          ) : social ? (
            <>
              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                  { label: 'Following', count: social.following_count, color: '#002FA7' },
                  { label: 'Followers', count: social.followers_count, color: '#002FA7' },
                  { label: 'Friends', count: social.friends_count, color: '#22C55E' },
                  { label: 'Subscriptions', count: social.subscriptions_count, color: '#F59E0B' },
                  { label: 'Pending', count: social.pending_count, color: '#EF4444' },
                ].map(s => (
                  <div key={s.label} className="bg-[var(--isp-glass)] border border-[var(--isp-glass-border)] rounded-xl p-4 text-center" data-testid={`social-stat-${s.label.toLowerCase()}`}>
                    <p className="text-2xl font-bold" style={{ color: s.color }}>{s.count}</p>
                    <p className="text-xs text-[var(--isp-muted)]">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Following */}
              <SectionCard title="Following" icon={UserPlus} iconColor="text-[#002FA7]">
                {social.following.length === 0 ? (
                  <p className="text-sm text-[var(--isp-muted)] py-4 text-center">You're not following anyone yet. Explore the <a href="/" className="text-[#002FA7] hover:underline">discovery feed</a> to find creators.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {social.following.map(u => (
                      <div key={u.id} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)]">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#002FA7] to-[#42A5F5] flex items-center justify-center text-white text-sm font-bold">{(u.name||'U')[0].toUpperCase()}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[var(--isp-heading)] truncate">{u.name}</p>
                          {u.institution && <p className="text-[10px] text-[var(--isp-muted)] truncate">{u.institution}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>

              {/* Followers */}
              <SectionCard title="Followers" icon={Users} iconColor="text-[#002FA7]">
                {social.followers.length === 0 ? (
                  <p className="text-sm text-[var(--isp-muted)] py-4 text-center">No followers yet. Share your recordings to grow your audience!</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {social.followers.map(u => (
                      <div key={u.id} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)]">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#002FA7] to-[#42A5F5] flex items-center justify-center text-white text-sm font-bold">{(u.name||'U')[0].toUpperCase()}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[var(--isp-heading)] truncate">{u.name}</p>
                          {u.institution && <p className="text-[10px] text-[var(--isp-muted)] truncate">{u.institution}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>

              {/* Friends */}
              <SectionCard title="Friends" icon={Heart} iconColor="text-[#22C55E]">
                {social.friends.length === 0 ? (
                  <p className="text-sm text-[var(--isp-muted)] py-4 text-center">No friends yet. Send friend requests from the <a href="/" className="text-[#002FA7] hover:underline">discovery feed</a>!</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {social.friends.map(u => (
                      <div key={u.id} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)]">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#22C55E] to-[#16A34A] flex items-center justify-center text-white text-sm font-bold">{(u.name||'U')[0].toUpperCase()}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[var(--isp-heading)] truncate">{u.name}</p>
                          {u.institution && <p className="text-[10px] text-[var(--isp-muted)] truncate">{u.institution}</p>}
                        </div>
                        <a href="/messages" className="text-xs text-[#002FA7] hover:underline flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Chat</a>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>

              {/* Subscriptions */}
              <SectionCard title="Subscriptions" icon={Star} iconColor="text-[#F59E0B]">
                {social.subscriptions.length === 0 ? (
                  <p className="text-sm text-[var(--isp-muted)] py-4 text-center">No subscriptions yet. Subscribe to recordings on the <a href="/" className="text-[#002FA7] hover:underline">discovery feed</a>.</p>
                ) : (
                  <div className="space-y-2">
                    {social.subscriptions.map(lec => (
                      <a key={lec.id} href={`/lectures?preview=${lec.id}`} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] hover:border-[#002FA7]/30 transition-all group">
                        <div className="w-9 h-9 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                          <Star className="w-4 h-4 text-[#F59E0B]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[var(--isp-heading)] truncate group-hover:text-[#002FA7]">{lec.title}</p>
                          <p className="text-[10px] text-[var(--isp-muted)]">by {lec.creator_name} {lec.subject_name ? `· ${lec.subject_name}` : ''}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </SectionCard>

              {/* Pending Friend Requests */}
              {social.pending_requests.length > 0 && (
                <SectionCard title="Pending Friend Requests" icon={UserPlus} iconColor="text-[#EF4444]">
                  <div className="space-y-2">
                    {social.pending_requests.map(r => (
                      <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)]">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#002FA7] to-[#42A5F5] flex items-center justify-center text-white text-sm font-bold">{(r.name||'U')[0].toUpperCase()}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[var(--isp-heading)] truncate">{r.name}</p>
                          {r.institution && <p className="text-[10px] text-[var(--isp-muted)]">{r.institution}</p>}
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/social/friend-request/${r.id}`);
                              toast.success(`You're now friends with ${r.name}!`);
                              loadSocial();
                            } catch {}
                          }}
                          className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#22C55E] text-white hover:bg-[#16A34A] transition-all"
                          data-testid={`accept-friend-${r.id}`}
                        >Accept</button>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              )}
            </>
          ) : (
            <p className="text-sm text-[var(--isp-muted)] text-center py-8">Unable to load social data. Try again.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
